import React, { useState, useEffect } from 'react';
import './productTableComponent.css';
import productUtils from '../../utils/product/product-utils';
import config from '../../config/tableConfig';
import Loader from '../loader/loaderComponent';
import Search from '../search/searchComponent';
import SearchBrand from '../search/brandSearchImage';
import PageControls from '../table-pagination/pageControls';
import SearchDropdown from '../search/searchDropdownComponent';
import discountUtils from '../../utils/discount';

const PAGE_SIZE = config.pageSize;

const ProductTable = (props) => {
  const [products, setProducts] = useState(null);
  const [visibleProducts, setVisibleProducts] = useState(null);
  const [brandFilteredProducts, setBrandFilteredProducts] = useState(null);
  const [page, setPageNumber] = useState(1);
  const [isFiltered, setIsFiltered] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [searchBrand, setSearchBrand] = useState(null);
  const [searchProduct, setSearchProduct] = useState(null);
  const [searchVisible, setSearchVisible] = useState(true);
  const [mergedDiscounts, setMergedDiscounts] = useState({ brands: {}, products: {} });
  const [showPromoOnly, setShowPromoOnly] = useState(false);
  const [promoFilteredProducts, setPromoFilteredProducts] = useState(null);

  useEffect(() => {
    async function getBusinessData() {
      const config = await productUtils.getBusinessConfig();
      return config;
    }
    getBusinessData().then(async (businessConfig) => {
      if (businessConfig.success) {
        const csvProducts = await productUtils.getCSV(
          businessConfig.data.productsURL
        );
        const JSONProducts = productUtils.csvToJSON(csvProducts);
        const firstPageProducts = JSONProducts.slice(0, PAGE_SIZE);
        setProducts(JSONProducts);
        setVisibleProducts(firstPageProducts);
        setIsLoaded(true);
      }
    });
  }, []);

  // fetch merged enabled discounts for quick lookup (runs on mount)
  useEffect(() => {
    let mounted = true;
    async function loadMerged() {
      try {
        const res = await discountUtils.getMergedEnabledDiscounts();
        if (!mounted) return;
        if (res && res.success && res.data) {
          setMergedDiscounts(res.data);
        } else {
          setMergedDiscounts({ brands: {}, products: {} });
        }
      } catch (err) {
        if (mounted) setMergedDiscounts({ brands: {}, products: {} });
      }
    }
    loadMerged();
    return () => { mounted = false; };
  }, []);

  const filterTable = (allProducts) => {
    let targetSearch = promoFilteredProducts || products;
    if (isFiltered && allProducts !== true) {
      targetSearch = visibleProducts;
    }
    if (
      isFiltered &&
      allProducts === true &&
      searchProduct &&
      (brandFilteredProducts || promoFilteredProducts)
    ) {
      targetSearch = brandFilteredProducts || promoFilteredProducts;
    }
    if (searchProduct && searchProduct.length > 0) {
      const words = searchProduct.split(' ');
      let filteredPartialResults = targetSearch;
      for (let i = 0; i < words.length; i++) {
        filteredPartialResults = filteredPartialResults.filter((el) => {
          let results = [];
          // Search by product description or ID
          const element =
            el[config.searchCriteria.product] &&
            el[config.searchCriteria.product].toLowerCase();
          const id = el[config.keyColumn];
          if (element && id) {
            results =
              element.includes(words[i].toLowerCase()) ||
              id.includes(words[i].toLowerCase());
          }
          return results;
        });
      }
      setVisibleProducts(filteredPartialResults);
      setIsFiltered(true);
    } else {
      if (brandFilteredProducts) {
        setVisibleProducts(brandFilteredProducts);
        setIsFiltered(true);
      } else if (promoFilteredProducts) {
        setVisibleProducts(promoFilteredProducts);
        setIsFiltered(true);
      } else {
        setVisibleProducts(products.slice(0, PAGE_SIZE));
        setIsFiltered(false);
      }
    }
  };

  const filterBrand = (allProducts, brand) => {
    let targetSearch = promoFilteredProducts || products;
    if (isFiltered && allProducts !== true) {
      targetSearch = visibleProducts;
    }

    if (brand !== undefined) {
      setSearchBrand(brand);
    }

    const search = brand || searchBrand;
    if (search && search.length > 0) {
      const filteredResults = targetSearch.filter((el) => {
        return (
          el[config.searchCriteria.brand] &&
          el[config.searchCriteria.brand].trim().toLowerCase() ===
            search.trim().toLowerCase()
        );
      });
      setVisibleProducts(filteredResults);
      setBrandFilteredProducts(filteredResults);
      setIsFiltered(true);
    } else {
      setBrandFilteredProducts(null);
      if (promoFilteredProducts) {
        setVisibleProducts(promoFilteredProducts);
        setIsFiltered(true);
      } else {
        setVisibleProducts(products.slice(0, PAGE_SIZE));
        setIsFiltered(false);
      }
    }
  };

  const hasPromo = (p) => {
    const brandName = (p[config.brandColumn] || '').toLowerCase();
    const productId = p[config.keyColumn] || '';
    const brandsKeys = Object.keys(mergedDiscounts.brands);
    const hasBrandDiscount = brandsKeys.some(b => b.toLowerCase() === brandName);
    const hasProductDiscount = !!mergedDiscounts.products[productId];
    return hasBrandDiscount || hasProductDiscount;
  };

  const togglePromoFilter = () => {
    if (showPromoOnly) {
      setShowPromoOnly(false);
      setPromoFilteredProducts(null);
      setSearchProduct('');
      if (brandFilteredProducts) {
        setVisibleProducts(brandFilteredProducts);
        setIsFiltered(true);
      } else {
        setVisibleProducts(products.slice(0, PAGE_SIZE));
        setIsFiltered(false);
      }
    } else {
      const base = brandFilteredProducts || products;
      const promoProducts = base.filter(hasPromo);
      setShowPromoOnly(true);
      setPromoFilteredProducts(promoProducts);
      setVisibleProducts(promoProducts);
      setSearchProduct('');
      setIsFiltered(true);
    }
  };

  const inputSearchBrand = (e) => {
    const brandStr = e.target.value;
    setSearchBrand(brandStr);
    if (!brandStr) {
      filterTable(true);
    }
  };

  const inputSearchProduct = (e) => {
    const productStr = e.target.value;
    setSearchProduct(productStr);
    if (!productStr) {
      filterBrand(true);
    }
  };

  const getProductById = (id) => {
    return products.find((elem) => elem[config.keyColumn] === id);
  };

  const navigateNextPage = () => {
    const maxPage = Math.ceil(products.length / PAGE_SIZE);
    const nextPage = page + 1;
    if (nextPage <= maxPage) {
      const nextVisibleItems = products.slice(
        page * PAGE_SIZE,
        nextPage * PAGE_SIZE
      );
      setVisibleProducts(nextVisibleItems);
      setPageNumber(nextPage);
    }
  };

  const navigatePrevPage = () => {
    const prevPage = page - 1;
    if (prevPage >= 1) {
      const previousVisibleItems = products.slice(
        (prevPage - 1) * PAGE_SIZE,
        prevPage * PAGE_SIZE
      );
      setVisibleProducts(previousVisibleItems);
      setPageNumber(prevPage);
    }
  };

  const addProductToCart = (id) => {
    // search product on the products state
    const product = getProductById(id);
    const productStock = parseInt(product[config.stockColumn], 10);

    // show the cart add and quantity
    const messageItem = `${product.Producto} - ${product.Marca} - ${product['Tamaño']}`;
    const quantity = prompt(`¿Cuantas unidades de ${messageItem} desea?`, '1');

    // Check if user is admin to buy with no stock checks
    const isAdmin = props.user && props.user.tags.includes('admin');
    // Some products are always purchasable regardless of stock
    const isStockExempt = config.stockExemptIds.includes(id);

    // If the user selects some quantity
    if (quantity) {
      const hasStock = productStock - parseInt(quantity, 10) >= 0;

      // Add check if already in the cart to update the quantity
      const currentCart = props.cart;
      const alreadyInCart = currentCart
        .getItems()
        .filter((item) => item.product[config.keyColumn] === id);
      if (alreadyInCart.length > 0) {
        const replaceQuantity = window.confirm(
          'El producto ya esta en el carrito. ¿Desea actualizar la cantidad?'
        );
        if (replaceQuantity) {
          currentCart.updateItemQuantity(id, quantity);
        }
        return;
      }

      // Add to the cart if has stock, is Admin, or product is stock-exempt
      if ((parseInt(quantity, 10) > 0 && hasStock) || isAdmin || isStockExempt) {
        if (!hasStock && isAdmin) {
          alert(
            `ATENCION: Esta agregando un producto ${messageItem} sin stock. Puede continuar porque es ADMIN.`
          );
        }
        // remove the - from the beginning and the % from the end to get the discount value
        const discountedPrice = product[config.promoColumn] ? parseFloat(product[config.promoColumn]
          .replace('-', '')
          .replace('%', '')) : null;
        let price = parseFloat(product[config.priceColumn]);
        if (discountedPrice) {
          const originalPrice = parseFloat(product[config.priceColumn]);
          const newPrice = Math.max(
            0,
            originalPrice - (originalPrice * parseFloat(discountedPrice)) / 100
          );
          price = newPrice.toFixed(2);
        }
        const subtotal = price * quantity;

        currentCart.addItem({
          product,
          Cantidad: quantity,
          Subtotal: subtotal,
        });
        props.cartCount(currentCart.getItems().length);
      } else if (!hasStock && config.stockCheck) {
        alert(`No hay stock del producto ${messageItem}`);
      } else {
        alert(`La cantidad ingresada no es válida.`);
      }
    }
  };

  const generateTable = (products) => {
    const headers = products ? Object.keys(products[0]) : null;

    const generateHeaders = (headers) => {
      const tableHeaders = [];
      if (headers) {
        for (let i = 0; i < headers.length; i++) {
          tableHeaders.push(
            <th
              className={
                config.noMobile[headers[i]]
                  ? 'text-center mobile-hidden'
                  : 'text-center'
              }
              key={`col-he-${i}`}
              scope="col"
            >
              {headers[i]}
            </th>
          );
        }
      }
      return tableHeaders;
    };

    const generateRowData = (product) => {
      const data = [];
      if (product[config.keyColumn]) {
        const keys = Object.keys(product);

        // Ensure promo column is included
        if (!keys.includes(config.promoColumn)) {
          keys.push(config.promoColumn);
        }

        // Add discount info if available for brand or product
        const brandName = product[config.brandColumn] || '';
        const productId = product[config.keyColumn] || '';

        let discountValue = null;
        const brandsKeys = Object.keys(mergedDiscounts.brands);
        const brandKeyLower = brandsKeys.map(b => b.toLowerCase());
        if (brandKeyLower.includes(brandName.toLowerCase())) {
          discountValue = mergedDiscounts.brands[brandsKeys[brandKeyLower.indexOf(brandName.toLowerCase())]].value;
        }
        if (mergedDiscounts.products[productId]) {
          discountValue = mergedDiscounts.products[productId].value;
        }

        // Apply discount if available
        let discountedPriceTotal = null;
        // console.log('Discount value for product', productId, 'is', discountValue);
        if (discountValue != null) {
          const originalPrice = parseFloat(product[config.priceColumn]);
          const discountedPrice = Math.max(
            0,
            originalPrice - (originalPrice * discountValue) / 100
          );
          discountedPriceTotal = discountedPrice.toFixed(2);
          product[config.promoColumn] = `-${discountValue}%`;
        } else {
          product[config.promoColumn] = '';
        }

        keys.forEach((key) => {
          const cellStyle = config.style[key] ? { textAlign: config.style[key] } : null;
          const cellClass = config.noMobile[key] ? 'mobile-hidden' : null;
          const hiddenColumn = config.hidden[key] ? { display: 'none' } : null;

          // If this is the price column and a promo price was computed, show original struck-through + new price in red
          if (key === config.priceColumn && discountedPriceTotal != null) {
            data.push(
              <td style={{ ...cellStyle, ...hiddenColumn }} className={cellClass} key={`${product[config.keyColumn]}-price`}>
                <div className="d-flex flex-column align-items-center">
                  <span style={{ color: '#d9534f', textDecoration: 'line-through', marginRight: 8 }}>
                    {productUtils.formatData(product[config.priceColumn], config.priceColumn)}
                  </span>
                  <span>
                    {productUtils.formatData(discountedPriceTotal, config.priceColumn)}
                  </span>
                </div>
              </td>
            );
          } else {
            data.push(
              <td style={{ ...cellStyle, ...hiddenColumn }} className={cellClass} key={`${product[key]}`}>
                {productUtils.formatData(product[key], key)}
              </td>
            );
          }
        });
      }
      return data;
    };

    const generateRows = (products) => {
      const productItems = [];
      if (products) {
        for (let i = 0; i < products.length; i++) {
          productItems.push(
            <tr
              className="row-clickeable"
              key={`row-${i}`}
              onClick={() => addProductToCart(products[i][config.keyColumn])}
            >
              {generateRowData(products[i])}
            </tr>
          );
        }
      }
      return productItems;
    };

    return (
      <table className="table table-sm table-responsive table-hover table-bordered table-custom">
        <thead className="thead-light">
          <tr>{generateHeaders(headers)}</tr>
        </thead>
        <tbody>{generateRows(visibleProducts)}</tbody>
      </table>
    );
  };

  const getUniqueBrands = (list) => {
    const seen = {};
    const brands = [];
    if (Array.isArray(list)) {
      for (let i = 0; i < list.length; i++) {
        const raw = list[i] && list[i]['Marca'] ? String(list[i]['Marca']).trim() : '';
        if (!raw) continue;
        const key = raw.toLowerCase();
        if (!seen[key]) {
          seen[key] = true;
          brands.push(raw);
        }
      }
    }
    // sort case-insensitively
    return brands.sort(function (a, b) {
      return a.toLowerCase().localeCompare(b.toLowerCase());
    });
  };

  const setSearchStatus = () => {
    setSearchVisible(!searchVisible);
  };

  const resetSearchStatus = () => {
    setSearchBrand(null);
    setSearchProduct('');
    setBrandFilteredProducts(null);
    setPromoFilteredProducts(null);
    setShowPromoOnly(false);
    setVisibleProducts(products.slice(0, PAGE_SIZE));
    setIsFiltered(false);
  };

  return (
    <div>
      <Loader show={!isLoaded} />
      <div style={{ display: !isLoaded ? 'none' : 'block' }}>
        <div className="card mt-3 mb-3 ml-0 mr-0">
          <div
            className="card-header pt-1 pb-2 pl-0 search-header"
            id="headingOne"
          >
            <h5 className="mb-0">
              <button
                className="btn search-header-button"
                data-toggle="collapse"
                data-target="#collapseOne"
                aria-expanded="true"
                aria-controls="collapseOne"
                onClick={setSearchStatus}
              >
                <i
                  className="material-icons icon-action icon-check collapsible-icon"
                  style={{ display: searchVisible ? 'none' : 'block' }}
                >
                  expand_less
                </i>
                <i
                  className="material-icons icon-action icon-check collapsible-icon"
                  style={{ display: searchVisible ? 'block' : 'none' }}
                >
                  expand_more
                </i>
                <i className="material-icons icon-action icon-check">search</i>
                Busqueda por Marcas y Productos
              </button>
            </h5>
          </div>

          <div
            id="collapseOne"
            className={searchVisible ? 'collapse show' : 'collapse'}
            aria-labelledby="headingOne"
            data-parent="#accordion"
          >
            <div className="card-body p-0">
              <SearchBrand
                brands={getUniqueBrands(products)}
                buttonCallback={filterBrand}
                selectedBrand={searchBrand}
                extraButtons={
                  <button
                    type="button"
                    className={`btn ml-1 mt-1 btn-brand ${showPromoOnly ? 'btn-danger' : 'btn-outline-danger'}`}
                    title="Ver solo promociones"
                    onClick={togglePromoFilter}
                    style={{ border: 'solid 1px', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <i className="material-icons" style={{ fontSize: '28px' }}>local_offer</i>
                    <span style={{ fontSize: '10px', marginTop: '2px' }}>
                      <span className="promo-label-full">Promociones KUM</span>
                      <span className="promo-label-short">Promo</span>
                    </span>
                  </button>
                }
              />
              <SearchDropdown
                text="Marca"
                includeButton={true}
                searchCallback={inputSearchBrand}
                buttonCallback={filterBrand}
                setSearchValueCallback={setSearchBrand}
                value={searchBrand}
                dropdownValues={getUniqueBrands(products)}
              />
              <Search
                text="Producto"
                includeButton={true}
                searchCallback={inputSearchProduct}
                buttonCallback={filterTable}
                value={searchProduct}
              />
              {isFiltered && (
                <div className="d-flex align-items-center m-2">
                  <button
                    className="btn btn-sm btn-dark btn-cart"
                    onClick={resetSearchStatus}
                  >
                    <span className="badge badge-pill badge-margin badge-danger mr-1">
                      {'X'}
                    </span>
                    Limpiar Busqueda
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        {isFiltered && visibleProducts && visibleProducts.length === 0 ? (
          <div
            style={{
              height: '560px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              backgroundColor: '#f8f9fa',
              color: '#6c757d',
            }}
          >
            <i className="material-icons" style={{ fontSize: '48px', marginBottom: '12px' }}>search_off</i>
            <span style={{ fontSize: '18px' }}>No hay resultados para esta búsqueda</span>
          </div>
        ) : (
          generateTable(products)
        )}
        <PageControls
          isFiltered={isFiltered}
          currentPage={page}
          nextPageHandler={navigateNextPage}
          prevPageHandler={navigatePrevPage}
          totalPages={products ? Math.ceil(products.length / PAGE_SIZE) : 0}
        />
      </div>
    </div>
  );
};

export default ProductTable;
